import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@foundation/components/ui/alert-dialog";
import { Plus, Search, LayoutGrid, TableIcon, RefreshCw } from "lucide-react";
import { clientService } from "../services/clientService";
import { ClientTable } from "../components/ClientTable";
import { ClientListView } from "../components/ClientListView";
import { ClientFormDialog } from "../components/ClientFormDialog";
import { CrmSidebar } from "../components/CrmSidebar";
import type { ClientRecord, ClientFieldsPayload, ViewMode } from "../types/clientTypes";

export const ClientsPage = () => {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("table");

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientRecord | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<ClientRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const records = await clientService.getClients();
      setClients(records);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredClients(clients);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredClients(
      clients.filter((c) =>
        [c.client_name, c.email, c.phone, c.address]
          .some((v) => v && v.toLowerCase().includes(q)),
      ),
    );
  }, [searchQuery, clients]);

  const handleCreate = () => {
    setEditingClient(null);
    setFormOpen(true);
  };

  const handleEdit = (client: ClientRecord) => {
    setEditingClient(client);
    setFormOpen(true);
  };

  const handleDeleteRequest = (client: ClientRecord) => {
    setDeleteTarget(client);
  };

  const handleFormSubmit = async (payload: ClientFieldsPayload) => {
    try {
      setIsSubmitting(true);
      if (editingClient) {
        await clientService.updateClient(editingClient.id, payload);
      } else {
        await clientService.createClient(payload);
      }
      setFormOpen(false);
      setEditingClient(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save client.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await clientService.deleteClient(deleteTarget.id);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete client.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <CrmSidebar />

      <div className="flex-1 overflow-y-auto min-w-0 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
            <p className="text-muted-foreground">Manage your client records</p>
          </div>
          <Button onClick={handleCreate} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="icon" onClick={fetchData} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              <TableIcon className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
              title="Card view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-md bg-destructive/10 p-4 text-destructive text-sm">
            {error}
            <Button
              variant="link"
              className="ml-2 text-destructive underline p-0 h-auto"
              onClick={() => setError(null)}
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Content */}
        {loading && !clients.length ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
            <span className="ml-3 text-muted-foreground">Loading...</span>
          </div>
        ) : viewMode === "table" ? (
          <ClientTable
            clients={filteredClients}
            onEdit={handleEdit}
            onDelete={handleDeleteRequest}
          />
        ) : (
          <ClientListView
            clients={filteredClients}
            onEdit={handleEdit}
            onDelete={handleDeleteRequest}
          />
        )}

        {/* Create / Edit dialog */}
        <ClientFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingClient(null);
          }}
          onSubmit={handleFormSubmit}
          client={editingClient}
          isSubmitting={isSubmitting}
        />

        {/* Delete confirmation */}
        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(open: boolean) => { if (!open) setDeleteTarget(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Client</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{" "}
                <strong>{deleteTarget?.client_name ?? "this client"}</strong>?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
